/**
 * Step 3 footer — back to Prepare, or forward to Step 4 (duration confirm).
 */

import { Link, useNavigate } from 'react-router-dom';
import { ROUTES } from '../../utils/createRoutes';
import { isAdminCreateSessionFlow } from '../../utils/createSessionFlowStorage';
import type { TimelineTopicItem } from '../../types/topicQuiz';
import {
  allTopicsHaveDuration,
  plannerTopicsToCurriculumTopics,
  saveCurriculumPathForTimeline,
} from '../../utils/curriculumPathStorage';
import { savePlannerTopics } from '../../utils/topicPlannerStorage';

interface PlannerFooterProps {
  topics?: TimelineTopicItem[];
  /** Persist workshop day schedules before navigating to Schedule. */
  onBeforeContinue?: () => void;
}

export function PlannerFooter({
  topics = [],
  onBeforeContinue,
}: PlannerFooterProps) {
  const navigate = useNavigate();
  const adminCreateFlow = isAdminCreateSessionFlow();
  const canContinue = allTopicsHaveDuration(topics);

  const handleBackToPrepare = () => {
    savePlannerTopics(topics);
    saveCurriculumPathForTimeline(plannerTopicsToCurriculumTopics(topics));
    navigate(ROUTES.prepareSession, {
      state: { continueFromPlanner: true },
    });
  };

  const handleContinue = () => {
    if (!canContinue) return;
    onBeforeContinue?.();
    navigate(ROUTES.scheduleSession);
  };

  return (
    <div className="codelive-wizard-actions">
      <button
        type="button"
        className="btn btn-outline-secondary"
        onClick={handleBackToPrepare}
      >
        Back to Prepare Session
      </button>
      {!adminCreateFlow && (
        <Link to={ROUTES.plannedSessions} className="btn btn-link">
          View Planned Sessions
        </Link>
      )}
      <button
        type="button"
        className="btn btn-primary"
        disabled={!canContinue}
        onClick={handleContinue}
        title={
          canContinue
            ? undefined
            : 'Add at least one subtopic to every topic and set a duration for each before continuing'
        }
      >
        {adminCreateFlow ? 'Confirm Duration & Schedule' : 'Schedule Session'}
      </button>
    </div>
  );
}
